import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";
import type { StatementRow } from "../../pages/pages/accounting/budgetTable";

export type BudgetStatus = "draft" | "approved";

export interface BudgetSummary {
  id: number;
  status: BudgetStatus;
  approvedAt: string | null;
  updatedAt?: string;
}

export interface BudgetLine {
  accountCode: number;
  amount: number;
  note?: string | null;
}

export interface BudgetFiscalYear {
  id: number;
  from: string;
  to: string;
  active: boolean;
  closedAt?: string | null;
}

export interface BudgetReport {
  budget: BudgetSummary | null;
  lines: BudgetLine[];
  period: { from: string; to: string };
  prevPeriod: { from: string; to: string } | null;
  budgetRows: StatementRow[];
  actual: StatementRow[];
  previousActual: StatementRow[] | null;
}

interface BudgetState {
  fiscalYears: BudgetFiscalYear[];
  report: BudgetReport | null;
  status: "idle" | "loading" | "failed";
  saving: boolean;
}

const initialState: BudgetState = {
  fiscalYears: [],
  report: null,
  status: "idle",
  saving: false,
};

type Rejection = { rejectValue: { message: string } };

const messageOf = (err: any, fallback: string): string =>
  err?.response?.data?.message ?? fallback;

/**
 * The page loads the fiscal years itself: the accounting slice only gets the
 * active year when the dashboard has been visited first.
 */
export const fetchBudgetFiscalYears = createAsyncThunk<
  BudgetFiscalYear[],
  number,
  Rejection
>("budget/fetchFiscalYears", async (associationId, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.get("/accounting/fiscal-years", {
      params: { associationId },
    });
    return data;
  } catch (err) {
    return rejectWithValue({
      message: messageOf(err, "Tilikausien haku epäonnistui."),
    });
  }
});

export const fetchBudgetReport = createAsyncThunk<
  BudgetReport,
  { associationId: number; fiscalYearId: number },
  Rejection
>("budget/fetchReport", async (params, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.get("/accounting/budgets/report", {
      params,
    });
    return data;
  } catch (err) {
    return rejectWithValue({
      message: messageOf(err, "Talousarvion haku epäonnistui."),
    });
  }
});

export const saveBudgetLines = createAsyncThunk<
  BudgetSummary,
  { associationId: number; fiscalYearId: number; lines: BudgetLine[] },
  Rejection
>(
  "budget/saveLines",
  async ({ associationId, fiscalYearId, lines }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(
        "/accounting/budgets/lines",
        { lines },
        { params: { associationId, fiscalYearId } }
      );
      return data;
    } catch (err) {
      return rejectWithValue({
        message: messageOf(err, "Talousarvion tallennus epäonnistui."),
      });
    }
  }
);

export const approveBudget = createAsyncThunk<
  BudgetSummary,
  { budgetId: number; approvedAt: string },
  Rejection
>("budget/approve", async ({ budgetId, approvedAt }, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.post(
      `/accounting/budgets/${budgetId}/approve`,
      { approvedAt }
    );
    return data;
  } catch (err) {
    return rejectWithValue({
      message: messageOf(err, "Hyväksytyksi merkitseminen epäonnistui."),
    });
  }
});

export const unapproveBudget = createAsyncThunk<
  BudgetSummary,
  number,
  Rejection
>("budget/unapprove", async (budgetId, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.post(
      `/accounting/budgets/${budgetId}/unapprove`
    );
    return data;
  } catch (err) {
    return rejectWithValue({
      message: messageOf(err, "Luonnokseksi palauttaminen epäonnistui."),
    });
  }
});

const setSummary = (state: BudgetState, summary: BudgetSummary) => {
  if (state.report) {
    state.report.budget = { ...state.report.budget, ...summary };
  }
};

const budgetSlice = createSlice({
  name: "budget",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBudgetFiscalYears.fulfilled, (state, action) => {
        state.fiscalYears = action.payload;
      })
      .addCase(fetchBudgetReport.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBudgetReport.fulfilled, (state, action) => {
        state.report = action.payload;
        state.status = "idle";
      })
      .addCase(fetchBudgetReport.rejected, (state) => {
        state.report = null;
        state.status = "failed";
      })
      .addCase(saveBudgetLines.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveBudgetLines.fulfilled, (state, action) => {
        state.saving = false;
        setSummary(state, action.payload);
      })
      .addCase(saveBudgetLines.rejected, (state) => {
        state.saving = false;
      })
      .addCase(approveBudget.fulfilled, (state, action) => {
        setSummary(state, action.payload);
      })
      .addCase(unapproveBudget.fulfilled, (state, action) => {
        setSummary(state, action.payload);
      });
  },
});

export default budgetSlice.reducer;
